import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { Star, Search, Loader2 } from 'lucide-react';
import { skillApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';

interface AddSkillDialogProps {
  employeeId: string;
  isOpen: boolean;
  onClose: (skillsAdded?: boolean) => void;
}

export const AddSkillDialog: React.FC<AddSkillDialogProps> = ({ employeeId, isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [selectedSkillIds, setSelectedSkillIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch employee skills
  const { data: employeeSkills = [] } = useQuery({
    queryKey: ["employeeSkills", employeeId],
    queryFn: () => skillApi.employeeSkills.getAll({ employee_id: employeeId }),
    enabled: isOpen && !!employeeId,
  });

  // Fetch all available skills from library
  const { data: allSkills = [] } = useQuery({
    queryKey: ["skills"],
    queryFn: () => skillApi.skills.getAll({}),
    enabled: isOpen,
  });

  // Fetch skill types (categories)
  const { data: skillTypes = [] } = useQuery({
    queryKey: ["skillTypes"],
    queryFn: () => skillApi.types.getAll(),
    enabled: isOpen,
  });

  // Bulk add skills mutation
  const bulkAddSkillMutation = useMutation({
    mutationFn: (data: any[]) => skillApi.employeeSkills.bulkCreate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["employeeSkills", employeeId],
      });
      setSelectedSkillIds(new Set());
      setSearchQuery("");
      toast({ title: "Skills added successfully" });
      onClose(true);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleAddSkills = () => {
    if (selectedSkillIds.size === 0) {
      toast({
        title: "Error",
        description: "Please select at least one skill",
        variant: "destructive",
      });
      return;
    }
    const skillsToAdd = Array.from(selectedSkillIds).map((skillId) => ({
      employee_id: employeeId,
      skill_id: skillId,
      proficiency_level: 5,
    }));
    bulkAddSkillMutation.mutate(skillsToAdd);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSelectedSkillIds(new Set());
      setSearchQuery("");
      onClose(false);
    }
  };

  // Get skills not already assigned to employee
  const availableSkills = allSkills.filter(
    (s: any) => !employeeSkills.some((es: any) => es.skill_id === s.id),
  );

  // Filter available skills by search query
  const filteredAvailableSkills = searchQuery
    ? availableSkills.filter((s: any) =>
        s.name.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : availableSkills;

  // Group filtered available skills by skill type
  const groupedSkills = useMemo(() => {
    const groups: { type: any; skills: any[] }[] = [];
    skillTypes.forEach((type: any) => {
      const typeSkills = filteredAvailableSkills.filter(
        (s: any) => s.skill_type_id === type.id,
      );
      if (typeSkills.length > 0) {
        groups.push({ type, skills: typeSkills });
      }
    });
    const ungrouped = filteredAvailableSkills.filter(
      (s: any) => !skillTypes.some((t: any) => t.id === s.skill_type_id),
    );
    if (ungrouped.length > 0) {
      groups.push({ type: { id: "__uncategorized", name: "Other" }, skills: ungrouped });
    }
    return groups;
  }, [skillTypes, filteredAvailableSkills]);

  const toggleSkill = (skillId: string) => {
    setSelectedSkillIds((prev) => {
      const next = new Set(prev);
      if (next.has(skillId)) {
        next.delete(skillId);
      } else {
        next.add(skillId);
      }
      return next;
    });
  };

  const toggleCategory = (categorySkills: any[]) => {
    const categorySkillIds = categorySkills.map((s: any) => s.id);
    const allSelected = categorySkillIds.every((id: string) => selectedSkillIds.has(id));

    setSelectedSkillIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        categorySkillIds.forEach((id: string) => next.delete(id));
      } else {
        categorySkillIds.forEach((id: string) => next.add(id));
      }
      return next;
    });
  };

  const isCategoryFullySelected = (categorySkills: any[]) => {
    return categorySkills.every((s: any) => selectedSkillIds.has(s.id));
  };

  const isCategoryPartiallySelected = (categorySkills: any[]) => {
    const someSelected = categorySkills.some((s: any) => selectedSkillIds.has(s.id));
    const allSelected = categorySkills.every((s: any) => selectedSkillIds.has(s.id));
    return someSelected && !allSelected;
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl border-none shadow-2xl bg-white dark:bg-slate-900 p-0 overflow-hidden rounded-3xl">
        <DialogHeader className="p-8 pb-4">
          <div className="flex items-center gap-3 mb-1">
            <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
              <Star className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <DialogTitle className="text-xl font-bold text-[#143A62] dark:text-white tracking-tight">Add Skills</DialogTitle>
          </div>
          <DialogDescription className="text-slate-500 dark:text-slate-400 font-medium">
            Select technical competencies from the company library to add to this profile.
          </DialogDescription>
        </DialogHeader>

        <div className="px-8 space-y-6">
          <div className="relative group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
            <Input
              placeholder="Search skills library..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-11 h-12 bg-slate-50 dark:bg-slate-800/50 border-slate-100 dark:border-slate-800 focus:border-blue-500 rounded-2xl transition-all"
            />
          </div>

          <div className="max-h-[400px] overflow-y-auto space-y-4 pr-2 custom-scrollbar">
            {groupedSkills.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="h-16 w-16 rounded-full bg-slate-50 dark:bg-slate-800 flex items-center justify-center mb-4">
                  <Search className="h-8 w-8 text-slate-300 dark:text-slate-600" />
                </div>
                <p className="text-sm font-bold text-slate-400 uppercase tracking-widest">No matching skills</p>
                <p className="text-xs text-slate-400 mt-1">Try a different search term or check the library.</p>
              </div>
            ) : (
              groupedSkills.map((group) => (
                <div key={group.type.id} className="overflow-hidden rounded-2xl bg-slate-50 dark:bg-slate-800/30 border border-slate-100 dark:border-slate-800">
                  <div className="flex items-center gap-3 px-5 py-4 bg-white/50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800">
                    <Checkbox
                      id={`category-${group.type.id}`}
                      checked={isCategoryFullySelected(group.skills)}
                      data-indeterminate={isCategoryPartiallySelected(group.skills)}
                      onCheckedChange={() => toggleCategory(group.skills)}
                      className="rounded-md border-slate-300 dark:border-slate-700 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                    />
                    <Label
                      htmlFor={`category-${group.type.id}`}
                      className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-widest cursor-pointer flex-1"
                    >
                      {group.type.name}
                      <span className="ml-2 text-[10px] text-slate-400 font-bold">
                        {group.skills.length} SKILLS
                      </span>
                    </Label>
                  </div>

                  <div className="p-2 grid grid-cols-1 md:grid-cols-2 gap-1">
                    {group.skills.map((skill: any) => (
                      <div
                        key={skill.id}
                        className={`flex items-center gap-3 px-3 py-2 rounded-xl transition-all cursor-pointer hover:bg-white dark:hover:bg-slate-800 shadow-sm border border-transparent ${selectedSkillIds.has(skill.id) ? 'bg-blue-50/50 dark:bg-blue-900/10 border-blue-100 dark:border-blue-900/30' : ''}`}
                        onClick={() => toggleSkill(skill.id)}
                      >
                        <Checkbox
                          id={`skill-${skill.id}`}
                          checked={selectedSkillIds.has(skill.id)}
                          onCheckedChange={() => toggleSkill(skill.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="rounded-md border-slate-300 dark:border-slate-700 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                        />
                        <Label
                          htmlFor={`skill-${skill.id}`}
                          className="text-xs font-bold text-slate-600 dark:text-slate-400 tracking-tight cursor-pointer flex-1"
                        >
                          {skill.name}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <DialogFooter className="p-8 mt-2 bg-slate-50/50 dark:bg-slate-900/50 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
          <div className="flex-1">
            {selectedSkillIds.size > 0 && (
              <div className="flex items-center gap-2">
                <Badge className="bg-blue-600 hover:bg-blue-700 text-white border-none font-black text-[10px] px-2 py-0.5 rounded-full">
                  {selectedSkillIds.size}
                </Badge>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Selected to add</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              onClick={() => handleOpenChange(false)}
              className="h-10 px-6"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddSkills}
              disabled={selectedSkillIds.size === 0 || bulkAddSkillMutation.isPending}
              className="h-10 px-8"
            >
              {bulkAddSkillMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Star className="h-4 w-4 mr-2" />
              )}
              Confirm Selection
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
